import React from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { Layout } from '../components/Layout';
import SEO from '../components/SEO';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';
import { Shield, Download, Trash2, AlertTriangle, Lock } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../components/ui/alert-dialog";

import { useAuth } from '../context/AuthContext';

export default function Settings() {
    const { user, updateUser } = useAuth();

    const handleTogglePrivacy = async (isPrivate) => {
        try {
            await updateUser({ is_private: isPrivate });
            toast.success(`Profile is now ${isPrivate ? 'private' : 'public'}`);
        } catch (error) {
            toast.error('Failed to update privacy settings');
        }
    };

    const handleExportData = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/privacy/export`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `myenab-data-export-${new Date().toISOString().split('T')[0]}.json`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            toast.success('Your data has been exported successfully.');
        } catch (error) {
            toast.error('Failed to export data');
        }
    };

    const handleDeleteAccount = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/privacy/delete-account`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Your account has been deleted.');
            // logout and redirect
            localStorage.removeItem('token');
            window.location.href = '/';
        } catch (error) {
            toast.error('Failed to delete account');
        }
    };

    return (
        <Layout>
            <SEO
                title="Settings"
                description="Manage your MyEnAb settings and privacy preferences."
            />
            <Toaster position="top-center" richColors />
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
                {/* Profile Privacy Section */}
                <Card className="bg-[#18181B] border-[#27272A]" data-testid="privacy-settings-card">
                    <CardHeader>
                        <CardTitle className="font-lexend flex items-center gap-2">
                            <Shield className="w-5 h-5 text-inclusion-blue" />
                            Profile Privacy
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                            <div>
                                <h3 className="text-white font-medium">Private Profile</h3>
                                <p className="text-zinc-400 text-sm mt-1">
                                    When enabled, only your connections can see your bio and disability categories.
                                </p>
                            </div>
                            <button
                                onClick={() => handleTogglePrivacy(!user?.is_private)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${user?.is_private ? 'bg-inclusion-blue' : 'bg-zinc-700'}`}
                            >
                                <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${user?.is_private ? 'translate-x-6' : 'translate-x-1'}`}
                                />
                            </button>
                        </div>
                    </CardContent>
                </Card>

                {/* Privacy & Data Section */}
                <Card className="bg-[#18181B] border-[#27272A]" data-testid="privacy-data-card">
                    <CardHeader>
                        <CardTitle className="font-lexend flex items-center gap-2">
                            <Shield className="w-5 h-5 text-inclusion-gold" />
                            Privacy & Your Data
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="bg-zinc-900/50 p-4 rounded-lg border border-zinc-800">
                            <h3 className="text-white font-medium mb-2 flex items-center gap-2">
                                <Download className="w-4 h-4" />
                                Export Personal Data
                            </h3>
                            <p className="text-zinc-400 text-sm mb-4">
                                Download a copy of all your personal data, including your profile information, messages, and connections in a JSON format.
                            </p>
                            <Button variant="ghost" className="btn-secondary w-full sm:w-auto" onClick={handleExportData}>
                                Export My Data
                            </Button>
                        </div>

                        <div className="bg-red-500/5 p-4 rounded-lg border border-red-500/20">
                            <h3 className="text-red-500 font-medium mb-2 flex items-center gap-2">
                                <Trash2 className="w-4 h-4" />
                                Delete Account
                            </h3>
                            <p className="text-zinc-400 text-sm mb-4">
                                Permanently delete your account and all associated data. This action is irreversible and you will lose access to all your connections and messages.
                            </p>
                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <Button variant="outline" className="border-red-500/50 text-red-500 hover:bg-red-500/10 w-full sm:w-auto">
                                        <AlertTriangle className="w-4 h-4 mr-2" />
                                        Delete My Account
                                    </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="bg-[#18181B] border-[#27272A] text-white">
                                    <AlertDialogHeader>
                                        <AlertDialogTitle className="text-xl font-lexend text-red-500">Are you absolutely sure?</AlertDialogTitle>
                                        <AlertDialogDescription className="text-zinc-400">
                                            This action cannot be undone. This will permanently delete your
                                            account and remove your data from our servers.
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white">Cancel</AlertDialogCancel>
                                        <AlertDialogAction 
                                            onClick={handleDeleteAccount}
                                            className="bg-red-600 hover:bg-red-700 text-white border-none"
                                        >
                                            Delete Account
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
